import { useMemo, useState } from 'react';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { format } from 'date-fns/format';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../stores';
import { useActiveTams } from '../../hooks/useActiveTams';
import { useAnnualTamPrimes } from '../../hooks/useAnnualTamPrimes';
import { usePrimesBreadcrumbContext } from '../../hooks/usePrimesBreadcrumbContext';
import { errorLogger } from '../../helpers/error';
import {
  ANNUAL_GOAL_COMPLETION_UI_ENABLED,
  getAnnualCurrentMonthHeaderState,
  getAnnualGoalHeaderState,
  mapAnnualTamPrimeToTableRows,
} from '../../utils/annualPrime.utils';
import { getTamDisplayName } from '../../utils/monthlyPrime.utils';
import AnnualTamPrimesHeader from '../molecules/AnnualTamPrimesHeader';
import AnnualTamPrimesTable from '../molecules/AnnualTamPrimesTable';

export type AnnualTamPrimesProps = {
  tamEmail?: string;
  referenceDate?: Date;
};

const AnnualTamPrimes = ({
  tamEmail: tamEmailOverride,
  referenceDate: referenceDateOverride,
}: AnnualTamPrimesProps) => {
  const { t } = useTranslation(['primes']);
  const auth = useAuthStore((state) => state.auth);
  const { getTamDisplayName: getActiveTamDisplayName } = useActiveTams();
  const authTamEmail = typeof auth?.user?.email === 'string' ? auth.user.email.trim() : '';
  const isTamPreview = Boolean(tamEmailOverride);
  const tamEmail = isTamPreview ? tamEmailOverride?.trim() : authTamEmail;
  const tamDisplayName = isTamPreview ? getActiveTamDisplayName(tamEmail ?? '') : getTamDisplayName(auth?.user);
  const initialYear = referenceDateOverride ? format(referenceDateOverride, 'yyyy') : format(new Date(), 'yyyy');
  const [selectedYear, setSelectedYear] = useState(() => initialYear);

  const { loading, error, aggregate } = useAnnualTamPrimes(tamEmail, selectedYear);
  const breadcrumbContext = useMemo(
    () => ({
      tamDisplayName: isTamPreview ? tamDisplayName : undefined,
      tamEmail: isTamPreview ? tamEmail : undefined,
      selectedYear,
    }),
    [isTamPreview, tamDisplayName, tamEmail, selectedYear],
  );
  usePrimesBreadcrumbContext(breadcrumbContext);
  const tableRows = useMemo(() => (aggregate ? mapAnnualTamPrimeToTableRows(aggregate) : []), [aggregate]);
  const currentMonth = useMemo(() => (aggregate ? getAnnualCurrentMonthHeaderState(aggregate) : null), [aggregate]);
  const goalState = useMemo(
    () => (ANNUAL_GOAL_COMPLETION_UI_ENABLED && aggregate ? getAnnualGoalHeaderState(aggregate) : null),
    [aggregate],
  );

  if (!tamEmail) {
    return <ErrorMessage variant="body1">{t('missingTamEmail')}</ErrorMessage>;
  }

  if (error) {
    errorLogger(error, { extraInfos: 'getTamPrimes failed on /primes/annuel' });
    return <ErrorMessage variant="body1">{t('annualLoadError')}</ErrorMessage>;
  }

  return (
    <Container>
      <AnnualTamPrimesHeader
        tamDisplayName={tamDisplayName}
        tamSeniority={aggregate?.tamSeniority}
        selectedYear={selectedYear}
        onSelectedYearChange={setSelectedYear}
        currentMonth={currentMonth}
        goalState={goalState}
        loading={loading}
      />
      <AnnualTamPrimesTable
        rows={tableRows}
        loading={loading}
        annualAggregate={aggregate}
        tamEmail={tamEmail}
        isTamPreview={isTamPreview}
      />
    </Container>
  );
};

export default AnnualTamPrimes;

const Container = styled('div')(({ theme }) => ({
  width: '100%',
  maxWidth: '100%',
  boxSizing: 'border-box',
  overflowX: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(3),
  [theme.breakpoints.down('sm')]: {
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
}));

const ErrorMessage = styled(Typography)(({ theme }) => ({
  marginTop: theme.spacing(3),
  color: theme.palette.error.main,
}));
